import React from 'react';
import { useTranslation } from 'react-i18next';
import { t as lnT } from '../i18n';

export const isDirectMarkdownVideo = source => {
    try {
        const url = new URL(source);
        return ['http:', 'https:'].includes(url.protocol) && /\.(mp4|webm|m4v|ogv|mov)$/i.test(url.pathname);
    } catch {
        return false;
    }
};

const getTwitterVideoId = source => {
    if (!isDirectMarkdownVideo(source)) return null;
    const url = new URL(source);
    return url.hostname.toLowerCase() === 'video.twimg.com'
        ? url.pathname.match(/^\/(amplify_video|ext_tw_video|tweet_video)\/([^/]+)\//)?.slice(1).join('/') || null
        : null;
};

const getSingleMedia = element => {
    if (!React.isValidElement(element) || ['code', 'pre'].includes(element.type)) return null;
    if (element.props.src || element.props.href) return element;
    const children = React.Children.toArray(element.props.children).filter(child => (
        typeof child !== 'string' || child.trim()
    ));
    return children.length === 1 ? getSingleMedia(children[0]) : null;
};

const addFallback = (element, media, fallbackHref) => {
    if (element === media) return React.cloneElement(element, { fallbackHref });
    return React.cloneElement(element, {}, React.Children.map(element.props.children, child => (
        React.isValidElement(child) ? addFallback(child, media, fallbackHref) : child
    )));
};

// Work on each compiler child list, including paragraphs separated by blank
// lines. This is pure so memoized Markdown options also work on later renders.
export const combineMarkdownVideoVariants = children => {
    const result = React.Children.toArray(children);
    for (let index = 0; index < result.length; index += 1) {
        const primary = getSingleMedia(result[index]);
        const videoId = getTwitterVideoId(primary?.props.src);
        if (!videoId) continue;
        let next = index + 1;
        while (typeof result[next] === 'string' && !result[next].trim()) next += 1;
        const fallback = getSingleMedia(result[next]);
        const label = React.Children.toArray(fallback?.props.children).join('').trim();
        if (!/^video file(?:\s+\d+)?$/i.test(label) || getTwitterVideoId(fallback?.props.href) !== videoId) continue;
        result[index] = addFallback(result[index], primary, fallback.props.href);
        result.splice(index + 1, next - index);
    }
    return result;
};

const VideoFallbackLink = ({ href, label, style }) => (
    <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        style={{
            display: 'inline-block',
            margin: '8px 0',
            color: style?.color || '#1f6fd6',
            textDecoration: 'underline',
            wordBreak: 'break-all',
            overflowWrap: 'break-word'
        }}
    >
        {label || lnT('messages:videoFile')}
    </a>
);

export const InlineVideoLink = ({ href, fallbackHref, label, style, surface = 'light' }) => {
    const {t: lnT} = useTranslation();

    const [failedSources, setFailedSources] = React.useState([]);
    const source = failedSources.includes(href) && fallbackHref && fallbackHref !== href ? fallbackHref : href;

    if (!href) {
        return null;
    }

    if (failedSources.includes(source)) {
        return <VideoFallbackLink href={source} label={lnT('messages:videoFile')} style={style} />;
    }

    return (
        <video
            key={source}
            src={source}
            aria-label={label || lnT('messages:videoFile')}
            controls
            preload="metadata"
            playsInline
            onClick={event => event.stopPropagation()}
            onError={() => setFailedSources(previous => [...previous, source])}
            style={{
                display: 'block',
                width: '100%',
                maxWidth: '560px',
                maxHeight: '420px',
                objectFit: 'contain',
                margin: '10px 0',
                borderRadius: '8px',
                background: surface === 'dark' ? '#050607' : '#000'
            }}
        >
            <a href={href} target="_blank" rel="noopener noreferrer">
                {label || lnT('messages:videoFile')}
            </a>
        </video>
    );
};

const MarkdownMediaWrapper = ({ children, ...props }) => {
    const combined = combineMarkdownVideoVariants(children);
    const hasBlocks = combined.some(child => React.isValidElement(child) && (
        child.type?.searchHighlightTraverseChildren ||
        ['p', 'ul', 'ol', 'pre', 'blockquote', 'div', 'table', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(child.type)
    ));
    const Element = hasBlocks ? 'div' : 'span';
    return <Element {...props}>{combined}</Element>;
};

MarkdownMediaWrapper.searchHighlightTraverseChildren = true;

export const createMarkdownMediaOptions = () => ({
    wrapper: MarkdownMediaWrapper,
    createElement: (tag, props, ...children) => React.createElement(tag, props, ...combineMarkdownVideoVariants(children))
});
